import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChuziApiError } from "@chuzi/shared/api";
import { t as translate } from "@chuzi/shared/config";
import { api } from "../lib/api";
import { auth } from "../lib/auth";
import { beginOidcLogin } from "../lib/oidc";
import { applyPendingSignupIfNeeded, clearPendingSignup, setPendingSignup } from "../lib/pending-signup";

type AuthFlow = "signup" | "signin";
type AuthStep = "credentials" | "verify";

function looksLikeEmail(value: string): boolean {
  return value.includes("@");
}

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [flow, setFlow] = useState<AuthFlow>("signin");
  const [step, setStep] = useState<AuthStep>("credentials");
  const [identifier, setIdentifier] = useState(searchParams.get("email") ?? "");
  const [email, setEmail] = useState(searchParams.get("email") ?? "");
  const [username, setUsername] = useState("");
  const [code, setCode] = useState("");
  const [identifierFocused, setIdentifierFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [usernameFocused, setUsernameFocused] = useState(false);
  const [codeFocused, setCodeFocused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [pendingIdentifier, setPendingIdentifier] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");

  const completeSignIn = useCallback(
    async (token?: string) => {
      if (token) {
        auth.setToken(token);
      }
      const profileEmail = pendingEmail || (looksLikeEmail(pendingIdentifier) ? pendingIdentifier : email);
      if (profileEmail) {
        await applyPendingSignupIfNeeded(profileEmail.trim());
      }
      clearPendingSignup();
      setMessage(translate(null, "auth_magic_link_verified", "Signed in. Redirecting..."));
      navigate("/my", { replace: true });
    },
    [email, navigate, pendingEmail, pendingIdentifier],
  );

  const verifyCode = useCallback(
    async (submittedCode: string) => {
      const trimmedCode = submittedCode.trim();
      if (!trimmedCode) {
        throw new Error(translate(null, "auth_code_required", "Sign-in code is required."));
      }

      const verifyBody = looksLikeEmail(pendingIdentifier)
        ? { email: pendingIdentifier.trim().toLowerCase(), token: trimmedCode, device_name: "chuzi-web" }
        : pendingEmail
          ? { email: pendingEmail.trim().toLowerCase(), token: trimmedCode, device_name: "chuzi-web" }
          : { identifier: pendingIdentifier.trim(), token: trimmedCode, device_name: "chuzi-web" };

      const body = await api.auth.verifyMagicLink(verifyBody);
      await completeSignIn(body.token);
    },
    [completeSignIn, pendingEmail, pendingIdentifier],
  );

  useEffect(() => {
    const signupEmail = searchParams.get("email");
    if (signupEmail) {
      setEmail(signupEmail);
      setIdentifier(signupEmail);
    }
  }, [searchParams]);

  useEffect(() => {
    const token = searchParams.get("token");
    const emailParam = searchParams.get("email");
    if (!token || !emailParam) {
      return;
    }

    setPendingIdentifier(emailParam);
    setPendingEmail(emailParam);
    setLoading(true);
    setError(null);
    void verifyCode(token)
      .catch((err) => {
        setStep("credentials");
        setCode("");
        setPendingIdentifier("");
        setPendingEmail("");
        setError(
          err instanceof ChuziApiError
            ? extractApiError(err)
            : err instanceof Error
              ? err.message
              : translate(null, "auth_magic_link_failed", "This magic link is invalid or expired."),
        );
        navigate("/login", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [navigate, searchParams, verifyCode]);

  async function ensureUniqueSignupUsername(input: string) {
    const trimmed = input.trim();
    if (!trimmed) {
      throw new Error(translate(null, "auth_username_required", "Username is required."));
    }

    setCheckingUsername(true);
    try {
      const availability = await api.auth.usernameAvailability(trimmed);
      if (availability.available) {
        return trimmed;
      }
      const suggestion =
        availability.suggestion && availability.suggestion.trim().length > 0
          ? availability.suggestion.trim()
          : `${trimmed}${Math.random().toString(36).slice(2, 3) || "x"}`;
      setUsername(suggestion);
      setMessage(
        translate(
          null,
          "auth_username_unavailable",
          "Username is taken; updated to an available suggestion.",
        ),
      );
      return suggestion;
    } finally {
      setCheckingUsername(false);
    }
  }

  async function requestMagicLinkSignIn(submittedIdentifier: string) {
    const trimmed = submittedIdentifier.trim();
    if (!trimmed) {
      throw new Error(translate(null, "auth_identifier_required", "Username or email is required."));
    }

    const requestBody = looksLikeEmail(trimmed)
      ? { email: trimmed.toLowerCase() }
      : { identifier: trimmed };

    await api.auth.requestMagicLink(requestBody);
    setPendingIdentifier(trimmed);
    setPendingEmail(looksLikeEmail(trimmed) ? trimmed.toLowerCase() : "");
    setStep("verify");
    setCode("");
    setMessage(
      translate(null, "auth_magic_link_sent_code", "Check your email for your sign-in code."),
    );
  }

  async function requestMagicLinkSignup(trimmedEmail: string, trimmedUsername: string) {
    await api.auth.requestMagicLink({
      email: trimmedEmail,
      username: trimmedUsername,
    });
    setPendingIdentifier(trimmedEmail);
    setPendingEmail(trimmedEmail);
    setStep("verify");
    setCode("");
    setMessage(
      translate(null, "auth_magic_link_sent_signup", "Check your email for your sign-up link."),
    );
  }

  async function onRequestSignIn(e?: FormEvent) {
    e?.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      clearPendingSignup();
      await requestMagicLinkSignIn(identifier);
    } catch (err) {
      setError(
        err instanceof ChuziApiError
          ? extractApiError(err)
          : err instanceof Error
            ? err.message
            : translate(null, "auth_login_failed", "Sign-in failed."),
      );
    } finally {
      setLoading(false);
    }
  }

  async function onRequestSignup(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const trimmedEmail = email.trim().toLowerCase();
      const trimmedUsername = await ensureUniqueSignupUsername(username);
      setPendingSignup({
        email: trimmedEmail,
        username: trimmedUsername,
      });
      await requestMagicLinkSignup(trimmedEmail, trimmedUsername);
    } catch (err) {
      setError(
        err instanceof ChuziApiError
          ? extractApiError(err)
          : err instanceof Error
            ? err.message
            : translate(null, "auth_login_failed", "Sign-in failed."),
      );
    } finally {
      setLoading(false);
    }
  }

  async function onVerifyCode(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      await verifyCode(code);
    } catch (err) {
      setError(
        err instanceof ChuziApiError
          ? extractApiError(err)
          : err instanceof Error
            ? err.message
            : translate(null, "auth_magic_link_failed", "This magic link is invalid or expired."),
      );
    } finally {
      setLoading(false);
    }
  }

  function resetToCredentials() {
    setStep("credentials");
    setCode("");
    setMessage(null);
    setError(null);
    setPendingIdentifier("");
    setPendingEmail("");
  }

  const shellStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    minHeight: "100dvh",
    padding: "96px 32px",
    backgroundColor: "#04070d",
    backgroundImage:
      "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.58) 22%, rgba(0,0,0,0.58) 32%, transparent 36%, transparent 64%, rgba(0,0,0,0.58) 68%, rgba(0,0,0,0.58) 78%, transparent 82%), radial-gradient(ellipse at top, rgba(14,30,66,0.6) 0%, rgba(4,7,13,0.95) 50%, #04070d 100%)",
    overflowY: "auto",
  };

  const formStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 20,
    width: "100%",
    maxWidth: 640,
    padding: "58px 56px",
    borderRadius: 26,
    background: "rgba(5, 10, 20, 0.74)",
    border: "1px solid rgba(126,184,255,0.34)",
    boxShadow: "0 34px 100px rgba(0,0,0,0.62)",
    backdropFilter: "blur(3px)",
  };

  if (step === "verify") {
    return (
      <div style={shellStyle}>
        <form onSubmit={onVerifyCode} style={formStyle}>
          <h1 style={headingStyle}>CHUZI</h1>
          <p style={subtitleStyle}>
            {translate(
              null,
              "auth_magic_link_enter_code",
              "Enter the code from your email to sign in.",
            )}
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder={translate(null, "auth_magic_link_code_placeholder", "Sign-in code")}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onFocus={() => setCodeFocused(true)}
            onBlur={() => setCodeFocused(false)}
            required
            disabled={loading}
            style={inputStyle(codeFocused)}
          />
          {message ? <p style={successTextStyle}>{message}</p> : null}
          {error ? <p style={errorTextStyle}>{error}</p> : null}
          <button type="submit" disabled={loading} style={submitStyle(loading)}>
            {loading
              ? translate(null, "auth_magic_link_verifying", "Verifying magic link...")
              : translate(null, "auth_sign_in_button", "Log in")}
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={resetToCredentials}
            style={secondaryButtonStyle}
          >
            {translate(null, "auth_magic_link_cta", "Use another email")}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={shellStyle}>
      <form
        onSubmit={flow === "signup" ? onRequestSignup : onRequestSignIn}
        style={formStyle}
      >
        <div style={tabRowStyle}>
          <button
            type="button"
            disabled={loading}
            onClick={() => {
              setFlow("signin");
              resetToCredentials();
            }}
            style={flowTabStyle(flow === "signin")}
          >
            {translate(null, "auth_signin_tab", "Sign in")}
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => {
              setFlow("signup");
              resetToCredentials();
            }}
            style={flowTabStyle(flow === "signup")}
          >
            {translate(null, "auth_signup_tab", "Sign up")}
          </button>
        </div>
        <h1 style={headingStyle}>CHUZI</h1>
        <p style={subtitleStyle}>
          {flow === "signup"
            ? translate(
                null,
                "auth_signup_subtitle",
                "Create your account with secure sign-in.",
              )
            : translate(
                null,
                "auth_signin_subtitle",
                "Sign in with your username or email.",
              )}
        </p>

        {flow === "signup" ? (
          <>
            <input
              type="email"
              autoComplete="email"
              placeholder={translate(null, "auth_email_placeholder", "Email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setEmailFocused(true)}
              onBlur={() => setEmailFocused(false)}
              required
              disabled={loading}
              style={inputStyle(emailFocused)}
            />
            <input
              type="text"
              autoComplete="username"
              placeholder={translate(null, "auth_username_placeholder", "Username")}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onFocus={() => setUsernameFocused(true)}
              onBlur={() => {
                setUsernameFocused(false);
                if (loading || checkingUsername) return;
                void ensureUniqueSignupUsername(username).catch((err) => {
                  setError(
                    err instanceof Error
                      ? err.message
                      : translate(
                          null,
                          "auth_username_update_failed",
                          "Could not reserve a unique username.",
                        ),
                  );
                });
              }}
              required
              minLength={2}
              maxLength={64}
              disabled={loading || checkingUsername}
              style={inputStyle(usernameFocused)}
            />
          </>
        ) : (
          <input
            type="text"
            autoComplete="username"
            placeholder={translate(null, "auth_identifier_placeholder", "Username or email")}
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            onFocus={() => setIdentifierFocused(true)}
            onBlur={() => setIdentifierFocused(false)}
            required
            disabled={loading}
            style={inputStyle(identifierFocused)}
          />
        )}

        {checkingUsername ? (
          <p style={successTextStyle}>
            {translate(null, "auth_username_checking", "Checking username availability...")}
          </p>
        ) : null}
        {message ? <p style={successTextStyle}>{message}</p> : null}
        {error ? <p style={errorTextStyle}>{error}</p> : null}

        {flow === "signin" ? (
          <>
            <button type="submit" disabled={loading || checkingUsername} style={submitStyle(loading)}>
              {loading
                ? translate(null, "auth_signing_in", "Signing in...")
                : translate(null, "auth_sign_in_button", "Log in")}
            </button>
            <button
              type="button"
              disabled={loading || checkingUsername}
              onClick={() => {
                setError(null);
                void beginOidcLogin().catch((err) => {
                  setError(
                    err instanceof Error
                      ? err.message
                      : translate(null, "auth_login_failed", "Sign-in failed."),
                  );
                });
              }}
              style={googleButtonStyle(loading)}
            >
              {translate(null, "auth_continue_with_google", "Continue with Google")}
            </button>
          </>
        ) : (
          <button type="submit" disabled={loading || checkingUsername} style={submitStyle(loading)}>
            {loading
              ? translate(null, "auth_signing_in", "Signing in...")
              : translate(null, "auth_sign_up_button", "Create account")}
          </button>
        )}
      </form>
    </div>
  );
}

function extractApiError(err: ChuziApiError): string {
  const body = err.body as { message?: string; errors?: Record<string, string[]> } | null;
  const first = body?.errors ? Object.values(body.errors)[0]?.[0] : null;
  return first ?? body?.message ?? translate(null, "auth_login_failed", "Sign-in failed.");
}

const headingStyle: React.CSSProperties = {
  margin: 0,
  color: "#7eb8ff",
  letterSpacing: 7,
  fontWeight: 900,
  fontSize: 64,
  lineHeight: 0.96,
};

const subtitleStyle: React.CSSProperties = {
  margin: 0,
  color: "rgba(232,240,255,0.86)",
  fontSize: 21,
  fontWeight: 800,
  lineHeight: 1.28,
  marginBottom: 8,
};

const tabRowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 8,
  marginBottom: 4,
};

const successTextStyle: React.CSSProperties = {
  margin: "2px 0 0",
  color: "#8af0ba",
  fontSize: 16,
  fontWeight: 700,
};

const errorTextStyle: React.CSSProperties = {
  margin: "2px 0 0",
  color: "#ff8888",
  fontSize: 16,
  fontWeight: 700,
};

function flowTabStyle(active: boolean): React.CSSProperties {
  return {
    border: `1px solid ${
      active ? "rgba(126,184,255,0.85)" : "rgba(126,184,255,0.32)"
    }`,
    borderRadius: 12,
    padding: "12px 14px",
    background: active ? "rgba(126,184,255,0.18)" : "rgba(8,14,28,0.72)",
    color: active ? "#cfe1ff" : "rgba(207,225,255,0.78)",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  };
}

function inputStyle(focused: boolean): React.CSSProperties {
  return {
    background: "#0a1020",
    color: "#e8f0ff",
    border: focused ? "1px solid rgba(126,184,255,0.88)" : "1px solid rgba(126,184,255,0.5)",
    borderRadius: 14,
    padding: "18px 22px",
    fontSize: 20,
    fontWeight: 700,
    outline: "none",
    boxShadow: focused ? "0 0 0 4px rgba(126,184,255,0.3)" : "none",
    transition: "border-color 140ms ease, box-shadow 140ms ease",
  };
}

function submitStyle(loading: boolean): React.CSSProperties {
  return {
    background: "#7eb8ff",
    color: "#04070d",
    border: 0,
    borderRadius: 14,
    padding: "18px 22px",
    fontSize: 19,
    fontWeight: 800,
    letterSpacing: 0.7,
    cursor: loading ? "wait" : "pointer",
    opacity: loading ? 0.85 : 1,
    marginTop: 8,
  };
}

function googleButtonStyle(loading: boolean): React.CSSProperties {
  return {
    background: "rgba(8,14,28,0.72)",
    color: "#e8f0ff",
    border: "1px solid rgba(126,184,255,0.52)",
    borderRadius: 14,
    padding: "18px 22px",
    fontSize: 17,
    fontWeight: 700,
    cursor: loading ? "wait" : "pointer",
    opacity: loading ? 0.85 : 1,
  };
}

const secondaryButtonStyle: React.CSSProperties = {
  border: "1px solid rgba(126,184,255,0.52)",
  borderRadius: 14,
  padding: "14px 18px",
  background: "rgba(8,14,28,0.72)",
  color: "#9fc7ff",
  fontSize: 16,
  fontWeight: 700,
  cursor: "pointer",
};
